import React, { useEffect, useMemo, useState } from "react";
import externalApiService from "../services/externalApiService";
import {
  ExternalForm,
  ExternalFormField,
  ExternalFormSection,
  ExternalSubmissionDetail,
  ExternalSubmissionListItem,
} from "../types";
import AlertModal from "../components/AlertModal";
import PageLoader from "../components/PageLoader";
import "./ExternalTickets.css";

type TabKey = "forms" | "submissions";

const ExternalTickets: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabKey>("forms");

  // Form akışı için durum
  const [forms, setForms] = useState<ExternalForm[]>([]);
  const [selectedForm, setSelectedForm] = useState<ExternalForm | null>(null);
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [files, setFiles] = useState<Record<string, File>>({});

  // Gönderim listesi (tablo) ve detay (modal) için durum
  const [submissions, setSubmissions] = useState<ExternalSubmissionListItem[]>(
    [],
  );
  const [search, setSearch] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("ALL");
  const [formFilter, setFormFilter] = useState<string>("ALL");
  const [detailOpen, setDetailOpen] = useState<boolean>(false);
  const [detailLoading, setDetailLoading] = useState<boolean>(false);
  const [detail, setDetail] = useState<ExternalSubmissionDetail | null>(null);

  // Genel
  const [loading, setLoading] = useState<boolean>(false);
  const [alert, setAlert] = useState<{
    isOpen: boolean;
    message: string;
    type: "info" | "success" | "warning" | "error";
  }>({ isOpen: false, message: "", type: "info" });

  const externalUser =
    process.env.REACT_APP_EXTERNAL_USER_REF || "system-default";

  // İlk yüklemede formları getir
  useEffect(() => {
    loadForms();
  }, []);

  // Sekme değiştikçe gönderim listesini yenile (her tab değişiminde tazele)
  useEffect(() => {
    if (activeTab === "submissions") {
      loadSubmissions();
    }
  }, [activeTab]);

  // ---- Veri çekme ----
  const loadForms = async () => {
    setLoading(true);
    try {
      const data = await externalApiService.getForms();
      setForms(data);
    } catch (error: any) {
      console.error("Forms loading error:", error);
      setAlert({
        isOpen: true,
        message:
          error.response?.data?.message ||
          "Talep formları yüklenirken hata oluştu.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadSubmissions = async () => {
    setLoading(true);
    try {
      const data = await externalApiService.getSubmissions();
      setSubmissions(data);
    } catch (error: any) {
      console.error("Submissions loading error:", error);
      setAlert({
        isOpen: true,
        message:
          error.response?.data?.message ||
          "Gönderimler yüklenirken hata oluştu.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const openDetail = async (submissionId: string) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setDetail(null);
    try {
      const data = await externalApiService.getSubmissionDetail(submissionId);
      setDetail(data);
    } catch (error: any) {
      console.error("Submission detail loading error:", error);
      setAlert({
        isOpen: true,
        message:
          error.response?.data?.message || "Gönderim detayı yüklenemedi.",
        type: "error",
      });
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setDetail(null);
  };

  // ---- Form gönderimi ----
  const handleFormSelect = (form: ExternalForm) => {
    setSelectedForm(form);
    setFormData({});
    setFiles({});
  };

  const handleInputChange = (fieldId: string, value: string) => {
    setFormData((prev) => ({ ...prev, [fieldId]: value }));
  };

  const handleFileChange = (fieldId: string, file: File | null) => {
    if (file) {
      setFiles((prev) => ({ ...prev, [fieldId]: file }));
    } else {
      setFiles((prev) => {
        const next = { ...prev };
        delete next[fieldId];
        return next;
      });
    }
  };

  const getAllFields = (): ExternalFormField[] => {
    if (!selectedForm) return [];
    const sectionFields = (selectedForm.sections || []).flatMap(
      (s) => s.fields,
    );
    return [...selectedForm.fields, ...sectionFields];
  };

  const validateForm = (): boolean => {
    if (!selectedForm) return false;

    for (const field of getAllFields()) {
      const isFileField = ["file", "photo", "video"].includes(field.fieldType);

      if (field.isRequired) {
        if (isFileField && !files[field.id]) {
          setAlert({
            isOpen: true,
            message: `"${field.label}" alanı zorunludur.`,
            type: "warning",
          });
          return false;
        }
        if (
          !isFileField &&
          (!formData[field.id] || formData[field.id].trim() === "")
        ) {
          setAlert({
            isOpen: true,
            message: `"${field.label}" alanı zorunludur.`,
            type: "warning",
          });
          return false;
        }
      }

      const file = files[field.id];
      if (file) {
        if (field.maxFileSizeMb) {
          const maxBytes = field.maxFileSizeMb * 1024 * 1024;
          if (file.size > maxBytes) {
            setAlert({
              isOpen: true,
              message: `"${field.label}" dosya boyutu ${field.maxFileSizeMb}MB'ı geçemez.`,
              type: "warning",
            });
            return false;
          }
        }
        if (field.allowedExtensions) {
          const ext = "." + file.name.split(".").pop()?.toLowerCase();
          const allowed = field.allowedExtensions
            .split(",")
            .map((e) => e.trim());
          if (!allowed.includes(ext)) {
            setAlert({
              isOpen: true,
              message: `"${field.label}" için sadece ${field.allowedExtensions} uzantılı dosyalar yüklenebilir.`,
              type: "warning",
            });
            return false;
          }
        }
      }
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedForm) return;
    if (!validateForm()) return;

    setLoading(true);
    try {
      await externalApiService.submitForm(selectedForm.id, formData, files);
      setAlert({
        isOpen: true,
        message: "Talebiniz başarıyla gönderildi.",
        type: "success",
      });
      setFormData({});
      setFiles({});
      setSelectedForm(null);
      // Sekme gönderimler ise listeyi tazele
      if (activeTab === "submissions") loadSubmissions();
    } catch (error: any) {
      console.error("Form submission error:", error);
      setAlert({
        isOpen: true,
        message:
          error.response?.data?.message ||
          "Talep gönderilirken hata oluştu.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // ---- Filtreleme ----
  const statusOptions = useMemo(() => {
    const map = new Map<string, string>();
    submissions.forEach((s) => {
      if (s.statusCode) map.set(s.statusCode, s.statusName || s.statusCode);
    });
    return Array.from(map.entries());
  }, [submissions]);

  const formOptions = useMemo(() => {
    const map = new Map<string, string>();
    submissions.forEach((s) => {
      if (s.formId) map.set(s.formId, s.formName || s.formId);
    });
    return Array.from(map.entries());
  }, [submissions]);

  const filteredSubmissions = useMemo(() => {
    const q = search.trim().toLocaleLowerCase("tr-TR");
    return submissions.filter((s) => {
      if (statusFilter !== "ALL" && s.statusCode !== statusFilter) return false;
      if (formFilter !== "ALL" && s.formId !== formFilter) return false;
      if (q) {
        const hay = `${s.formName} ${s.statusName}`.toLocaleLowerCase("tr-TR");
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [submissions, search, statusFilter, formFilter]);

  // ---- Yardımcılar ----
  const statusClass = (code: string): string => {
    switch (code) {
      case "OPEN":
        return "tk-status tk-status-open";
      case "IN_PROGRESS":
        return "tk-status tk-status-progress";
      case "RESOLVED":
        return "tk-status tk-status-resolved";
      case "CLOSED":
        return "tk-status tk-status-closed";
      default:
        return "tk-status tk-status-default";
    }
  };

  const formatDate = (iso: string): string => {
    try {
      return new Date(iso).toLocaleString("tr-TR");
    } catch {
      return iso;
    }
  };

  // ---- Form alanı render ----
  const renderField = (field: ExternalFormField) => {
    const widthClass =
      field.width === "half" ? "tk-field tk-field-half" : "tk-field tk-field-full";
    const requiredClass = field.isRequired ? " tk-field-required" : "";
    const isFileField = ["file", "photo", "video"].includes(field.fieldType);

    const label = (
      <label htmlFor={field.id}>
        {field.label}
        {field.isRequired && <span className="tk-required">*</span>}
      </label>
    );

    let control: React.ReactNode = null;

    if (isFileField) {
      control = (
        <>
          <input
            type="file"
            id={field.id}
            accept={field.allowedExtensions || undefined}
            onChange={(e) =>
              handleFileChange(field.id, e.target.files?.[0] || null)
            }
            className="tk-file-input"
          />
          {(field.maxFileSizeMb || field.allowedExtensions) && (
            <small className="tk-field-hint">
              {field.maxFileSizeMb && `Maks. ${field.maxFileSizeMb} MB`}
              {field.maxFileSizeMb && field.allowedExtensions && " · "}
              {field.allowedExtensions && `İzinli: ${field.allowedExtensions}`}
            </small>
          )}
          {files[field.id] && (
            <div className="tk-file-preview">
              <span className="tk-file-name">{files[field.id].name}</span>
              <span className="tk-file-size">
                ({(files[field.id].size / 1024).toFixed(1)} KB)
              </span>
            </div>
          )}
        </>
      );
    } else if (field.fieldType === "textarea") {
      control = (
        <textarea
          id={field.id}
          className="tk-textarea"
          value={formData[field.id] || ""}
          placeholder={field.placeholder || ""}
          onChange={(e) => handleInputChange(field.id, e.target.value)}
          rows={4}
        />
      );
    } else if (field.fieldType === "select") {
      control = (
        <select
          id={field.id}
          className="tk-select"
          value={formData[field.id] || ""}
          onChange={(e) => handleInputChange(field.id, e.target.value)}
        >
          <option value="">Seçiniz...</option>
          {field.options.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      );
    } else if (field.fieldType === "radio") {
      control = (
        <div className="tk-radio-group">
          {field.options.map((opt) => (
            <label key={opt} className="tk-radio">
              <input
                type="radio"
                name={field.id}
                value={opt}
                checked={formData[field.id] === opt}
                onChange={(e) => handleInputChange(field.id, e.target.value)}
              />
              <span>{opt}</span>
            </label>
          ))}
        </div>
      );
    } else if (field.fieldType === "checkbox") {
      control = (
        <div className="tk-checkbox-group">
          {field.options.map((opt) => {
            const selected = formData[field.id]
              ? formData[field.id].split(",")
              : [];
            const isChecked = selected.includes(opt);
            return (
              <label key={opt} className="tk-checkbox">
                <input
                  type="checkbox"
                  value={opt}
                  checked={isChecked}
                  onChange={(e) => {
                    let next = [...selected];
                    if (e.target.checked) next.push(opt);
                    else next = next.filter((v) => v !== opt);
                    handleInputChange(field.id, next.join(","));
                  }}
                />
                <span>{opt}</span>
              </label>
            );
          })}
        </div>
      );
    } else if (field.fieldType === "date") {
      control = (
        <input
          type="date"
          id={field.id}
          className="tk-input"
          value={formData[field.id] || ""}
          onChange={(e) => handleInputChange(field.id, e.target.value)}
        />
      );
    } else if (field.fieldType === "number") {
      control = (
        <input
          type="number"
          id={field.id}
          className="tk-input"
          value={formData[field.id] || ""}
          placeholder={field.placeholder || ""}
          onChange={(e) => handleInputChange(field.id, e.target.value)}
        />
      );
    } else {
      control = (
        <input
          type="text"
          id={field.id}
          className="tk-input"
          value={formData[field.id] || ""}
          placeholder={field.placeholder || ""}
          onChange={(e) => handleInputChange(field.id, e.target.value)}
        />
      );
    }

    return (
      <div key={field.id} className={widthClass + requiredClass}>
        {label}
        {control}
      </div>
    );
  };

  const renderSection = (section: ExternalFormSection) => {
    const sorted = [...section.fields].sort(
      (a, b) => a.sortOrder - b.sortOrder,
    );
    return (
      <div key={section.id} className="tk-form-section">
        <div className="tk-form-section-header">
          <h3 className="tk-form-section-title">{section.title}</h3>
          {section.description && (
            <p className="tk-form-section-desc">{section.description}</p>
          )}
        </div>
        <div className="tk-form-fields">{sorted.map(renderField)}</div>
      </div>
    );
  };

  if (loading && forms.length === 0 && submissions.length === 0) {
    return <PageLoader />;
  }

  return (
    <div className="tk-page">
      {/* Header */}
      <div className="tk-header">
        <div className="tk-header-left">
          <div className="tk-header-icon">
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
              <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
              <line x1="12" y1="22.08" x2="12" y2="12"></line>
            </svg>
          </div>
          <div className="tk-header-text">
            <h1>Geliştirme Talepleri & Öneriler</h1>
            <p>
              Yeni özellik talepleri ve önerilerinizi iletin, gönderimlerinizi
              tek ekrandan takip edin.
            </p>
          </div>
        </div>
        <span className="tk-user-badge" title="Bağlı kullanıcı">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
            <circle cx="12" cy="7" r="4"></circle>
          </svg>
          {externalUser}
        </span>
      </div>

      {/* Tabs */}
      <div className="tk-tabs" role="tablist">
        <button
          className={`tk-tab ${activeTab === "forms" ? "is-active" : ""}`}
          onClick={() => setActiveTab("forms")}
          type="button"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
            <polyline points="14 2 14 8 20 8"></polyline>
          </svg>
          Talep Formları
          <span className="tk-count">{forms.length}</span>
        </button>
        <button
          className={`tk-tab ${activeTab === "submissions" ? "is-active" : ""}`}
          onClick={() => setActiveTab("submissions")}
          type="button"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
          </svg>
          Taleplerim
          <span className="tk-count">{submissions.length}</span>
        </button>
      </div>

      {/* FORMS TAB */}
      {activeTab === "forms" && (
        <div className="tk-forms-grid">
          <div className="tk-panel">
            <div className="tk-panel-header">
              <h2>Talep Formları</h2>
            </div>
            <div className="tk-panel-body">
              {forms.length === 0 ? (
                <div className="tk-empty-detail">
                  <svg
                    width="56"
                    height="56"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  >
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                  </svg>
                  <p>Henüz form bulunmuyor</p>
                </div>
              ) : (
                <div className="tk-form-list">
                  {forms.map((form) => (
                    <div
                      key={form.id}
                      className={`tk-form-card ${
                        selectedForm?.id === form.id ? "is-selected" : ""
                      }`}
                      onClick={() => handleFormSelect(form)}
                    >
                      <h3>{form.name}</h3>
                      {form.description && <p>{form.description}</p>}
                      <span className="tk-field-count">
                        {form.fields.length} alan
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="tk-panel">
            {selectedForm ? (
              <div className="tk-form-detail">
                <div className="tk-form-detail-header">
                  <h2>{selectedForm.name}</h2>
                  {selectedForm.description && (
                    <p>{selectedForm.description}</p>
                  )}
                </div>
                <form className="tk-form" onSubmit={handleSubmit}>
                  {getAllFields().some((f) => f.isRequired) && (
                    <p className="tk-required-note">
                      <span className="tk-required">*</span> ile işaretli alanların doldurulması zorunludur.
                    </p>
                  )}
                  {selectedForm.fields.length > 0 && (
                    <div className="tk-form-fields">
                      {[...selectedForm.fields]
                        .sort((a, b) => a.sortOrder - b.sortOrder)
                        .map(renderField)}
                    </div>
                  )}
                  {[...(selectedForm.sections || [])]
                    .sort((a, b) => a.sortOrder - b.sortOrder)
                    .map(renderSection)}

                  <div className="tk-form-actions">
                    <button
                      type="button"
                      className="tk-btn tk-btn-secondary"
                      onClick={() => {
                        setSelectedForm(null);
                        setFormData({});
                        setFiles({});
                      }}
                    >
                      İptal
                    </button>
                    <button
                      type="submit"
                      className="tk-btn tk-btn-primary"
                      disabled={loading}
                    >
                      {loading ? "Gönderiliyor..." : "Gönder"}
                    </button>
                  </div>
                </form>
              </div>
            ) : (
              <div className="tk-empty-detail">
                <svg
                  width="64"
                  height="64"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                >
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                  <line x1="12" y1="11" x2="12" y2="17"></line>
                  <line x1="9" y1="14" x2="15" y2="14"></line>
                </svg>
                <p>Bir form seçerek doldurmaya başlayın</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* SUBMISSIONS TAB */}
      {activeTab === "submissions" && (
        <>
          <div className="tk-toolbar">
            <div className="tk-search">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
              <input
                type="text"
                placeholder="Form adı veya durumda ara..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <div className="tk-filter">
              <label>Form</label>
              <select
                value={formFilter}
                onChange={(e) => setFormFilter(e.target.value)}
              >
                <option value="ALL">Tümü</option>
                {formOptions.map(([id, name]) => (
                  <option key={id} value={id}>
                    {name}
                  </option>
                ))}
              </select>
            </div>

            <div className="tk-filter">
              <label>Durum</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="ALL">Tümü</option>
                {statusOptions.map(([code, name]) => (
                  <option key={code} value={code}>
                    {name}
                  </option>
                ))}
              </select>
            </div>

            <div className="tk-toolbar-meta">
              <strong>{filteredSubmissions.length}</strong> / {submissions.length}{" "}
              kayıt
            </div>
          </div>

          <div className="tk-table-wrap">
            {filteredSubmissions.length === 0 ? (
              <div className="tk-table-empty">
                <svg
                  width="56"
                  height="56"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                >
                  <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                </svg>
                <p>Kayıt bulunamadı</p>
                <small>
                  {submissions.length === 0
                    ? "Henüz bir gönderim oluşturulmamış."
                    : "Arama veya filtre kriterlerinizi değiştirmeyi deneyin."}
                </small>
              </div>
            ) : (
              <div className="tk-table-scroll">
                <table className="tk-table">
                  <thead>
                    <tr>
                      <th>Form</th>
                      <th>Durum</th>
                      <th>Gönderilme</th>
                      <th>Son Güncellenme</th>
                      <th className="tk-td-actions">İşlem</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSubmissions.map((s) => (
                      <tr key={s.id}>
                        <td className="tk-td-form">{s.formName}</td>
                        <td>
                          <span className={statusClass(s.statusCode)}>
                            {s.statusName || s.statusCode || "—"}
                          </span>
                        </td>
                        <td className="tk-td-date">
                          {formatDate(s.submittedAt)}
                        </td>
                        <td className="tk-td-date">
                          {formatDate(s.updatedAt)}
                        </td>
                        <td className="tk-td-actions">
                          <button
                            type="button"
                            className="tk-inspect-btn"
                            onClick={() => openDetail(s.id)}
                          >
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                              <circle cx="12" cy="12" r="3"></circle>
                            </svg>
                            İncele
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* DETAY MODAL */}
      {detailOpen && (
        <div className="tk-modal-overlay" onClick={closeDetail}>
          <div className="tk-modal" onClick={(e) => e.stopPropagation()}>
            <div className="tk-modal-header">
              <div className="tk-modal-title">
                <h3>{detail?.formName || "Gönderim Detayı"}</h3>
                {detail && (
                  <div className="tk-modal-sub">
                    <span>
                      <strong>Durum:</strong>{" "}
                      <span className={statusClass(detail.statusCode)}>
                        {detail.statusName || detail.statusCode || "—"}
                      </span>
                    </span>
                    <span>
                      <strong>Gönderilme:</strong> {formatDate(detail.submittedAt)}
                    </span>
                    <span>
                      <strong>Güncellenme:</strong> {formatDate(detail.updatedAt)}
                    </span>
                  </div>
                )}
              </div>
              <button
                type="button"
                className="tk-modal-close"
                onClick={closeDetail}
                aria-label="Kapat"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            <div className="tk-modal-body">
              {detailLoading || !detail ? (
                <div className="tk-empty-detail">
                  <p>Detay yükleniyor...</p>
                </div>
              ) : (
                <>
                  <div className="tk-modal-section">
                    <h4 className="tk-modal-section-title">Form Değerleri</h4>
                    {detail.values.length === 0 ? (
                      <p className="tk-field-hint">Değer bulunmuyor.</p>
                    ) : (
                      <div className="tk-kv-grid">
                        {detail.values.map((v) => (
                          <div key={v.fieldId} className="tk-kv">
                            <span className="tk-kv-label">{v.label}</span>
                            <span className="tk-kv-value">{v.value || "—"}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {detail.files.length > 0 && (
                    <div className="tk-modal-section">
                      <h4 className="tk-modal-section-title">Dosyalar</h4>
                      <div className="tk-modal-files">
                        {detail.files.map((f) => (
                          <div key={f.fieldId} className="tk-modal-file">
                            <div className="tk-modal-file-info">
                              <span className="tk-mf-label">{f.label}</span>
                              <span className="tk-mf-name">
                                {f.originalFileName}
                              </span>
                              <span className="tk-mf-size">
                                {(f.sizeBytes / 1024).toFixed(1)} KB
                              </span>
                            </div>
                            <a
                              href={f.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="tk-btn tk-btn-primary"
                            >
                              <svg
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              >
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                <polyline points="7 10 12 15 17 10"></polyline>
                                <line x1="12" y1="15" x2="12" y2="3"></line>
                              </svg>
                              İndir
                            </a>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="tk-modal-footer">
              <button
                type="button"
                className="tk-btn tk-btn-secondary"
                onClick={closeDetail}
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}

      <AlertModal
        isOpen={alert.isOpen}
        message={alert.message}
        type={alert.type}
        onClose={() => setAlert({ ...alert, isOpen: false })}
      />
    </div>
  );
};

export default ExternalTickets;
